import _ from 'lodash';
import { RequestInit } from 'node-fetch';
import Path from 'path';
import Url from 'url';
// @ts-ignore
import CryptoJS from 'crypto-js';
import showAlert from './showAlert';
import throttlingPromise from '@mpc/common/src/utils/throttlingPromise';
import ServiceError from '@mpc/common/src/utils/serviceError';
import { adaptMpcExternalOuterPath } from '@mpc/common/src/utils/axios';
import { getVendorStorage } from '@mpc/common/src/utils';

// 幂等相关
const REQUEST_UUID_MAP = new Map();
const createUUID = () => {
  let dt = new Date().getTime();
  const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (dt + Math.random() * 16) % 16 | 0;
    dt = Math.floor(dt / 16);
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
  return uuid;
};
const setRequestUUID = (ajaxParams: any) => {
  const { url = '', method = '', body = {}, headers = {} } = ajaxParams || {};
  const bodyJson = JSON.stringify(body || {});
  const bodyJsonHash = CryptoJS.SHA1(bodyJson).toString();
  const uuidKey = `${method}:${url || ''}@${bodyJsonHash}`;

  if (!REQUEST_UUID_MAP.get(uuidKey)) {
    REQUEST_UUID_MAP.set(uuidKey, createUUID());
  }

  const uuidValue = REQUEST_UUID_MAP.get(uuidKey);

  // Object.assign(ajaxParams.body || {}, { uuid: uuidValue });
  // Object.assign(ajaxParams.headers || {}, { "X-MPC-UUID" : uuidValue });
  ajaxParams.headers['X-MPC-UUID'] = uuidValue;

  return uuidKey;
};
const removeRequestUUID = (uuidKey: string) => {
  REQUEST_UUID_MAP.delete(uuidKey);
};

export interface IajaxParams extends RequestInit {
  url: string;
  body?: any;
  credentials?: 'same-origin' | 'omit' | 'include';
  toast?: any;
  dataType?: string;
  fileName?: string;
  withUUID?: boolean;
}

const ajax = throttlingPromise(function (params: IajaxParams, showErrorAlert = true) {
  const options = Object.assign(
    {
      method: 'GET',
      body: {},
      dataType: 'json',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Content-Type': 'application/json; charset=utf-8',
      },
      credentials: 'same-origin',
    },
    params,
  );

  const { body, method, withUUID = true } = options;

  let uuidKey = '';
  if (withUUID && ['POST', 'PUT', 'DELETE'].includes(method)) {
    uuidKey = setRequestUUID(options);
  }

  if (method === 'GET') {
    const urlObject = Url.parse(options.url);
    // @ts-ignore
    urlObject.query = Object.assign({}, urlObject.query || {}, body);
    options.url = Url.format(urlObject);
    delete options.body;
  } else {
    options.body = JSON.stringify(body);
  }

  // 接口添加前端版本号
  options.headers['x-fe-version-tag'] = window.$versionTag || 'dev'
  const { vendorId, tenantCode } = getVendorStorage();
  // @ts-ignore
  options.headers['Vendor-Id'] = vendorId || ''
  // @ts-ignore
  options.headers['Tenant-Code'] = tenantCode || ''

  const { url, toast, ...otherOptions } = options;
  let { dataType } = options;

  if (process.env.NODE_ENV === 'development') {
    options.headers['User'] = 1536;
    options.headers['SupplierUser'] = 86;
  }

  let urlPath = url.startsWith('/api') ? url : Path.join('/api', url);
  urlPath = adaptMpcExternalOuterPath(urlPath);
   // @ts-ignore
  return fetch(urlPath, otherOptions)
    .then((response: any) => {
      if (!response.ok) {
        const error = new Error(response.statusText);
        // @ts-ignore
        error.response = response;
        throw error;
      }

      const headers = response.headers;
      const contentDisposition = headers.get('Content-Disposition');
      // 如果返回文件，检测返回头是否有文件标识
      if (dataType === 'blob') {
        if (contentDisposition) {
          if (method === 'POST') {
            return postDownload(response);
          } else {
            return response[dataType]().then((data: any) => {
              if (options.fileName) {
                return data;
              }
            });
          }
        } else {
          dataType = 'json';
          return response.json();
        }
      }
      return response[dataType]();
    })
    .then((result: any) => {
      if (dataType === 'json') {
        // 兼容java接口
        if (_.isNil(result.res_code) && !_.isNil(result.code)) {
          result.res_code = result.code;
        }
        // 兼容java接口
        if (!result.res_msg && result.message) {
          result.res_msg = result.message;
        }

        if (result.res_code !== 1 && result.res_code !== -1) {
          const resMsg = Array.isArray(result.res_msg)
            ? result.res_msg.join(';')
            : result.res_msg;
          throw new Error(resMsg);
        }

        if (toast && toast.success) {
          showAlert({ type: 'success', message: result.res_msg });
        }
      }

      if (uuidKey) {
        removeRequestUUID(uuidKey);
      }

      return result;
    })
    .catch((error: any) => {
      showErrorAlert &&
        showAlert({
          type: 'error',
          message:
            typeof error === 'string' ? error : error.message || '未知错误',
        });
        throw new ServiceError(typeof error === 'string' ? error : error.message || '服务器错误')
    });
})

export default ajax

export interface IajaxParamsOut extends IajaxParams {
  isOut?: boolean;
  version?: string;
}

export const ajaxJAVA =  throttlingPromise(function(params: IajaxParamsOut, showErrorAlert = true) {
  // 默认参数
  params = Object.assign({
    isOut: true,
    version: 'v2',
  }, params);
  const options = Object.assign(
    {
      method: 'GET',
      body: {},
      dataType: 'json',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Content-Type': 'application/json; charset=utf-8',
      },
      credentials: 'same-origin',
    },
    params,
  );

  const { body, method, withUUID = true } = options;

  if(options.isOut) {
    const version = options.version || 'v1';
    options.url = `outsource/${version}/${options.url}`;
  }

  let uuidKey = '';
  if (withUUID && ['POST', 'PUT', 'DELETE'].includes(method)) {
    uuidKey = setRequestUUID(options);
  }

  if (method === 'GET') {
    const urlObject = Url.parse(options.url);
    // @ts-ignore
    urlObject.query = Object.assign({}, urlObject.query || {}, body);
    options.url = Url.format(urlObject);
    delete options.body;
  } else {
    options.body = JSON.stringify(body);
  }

  const { url, toast, ...otherOptions } = options;
  let { dataType } = options;

  if (process.env.NODE_ENV === 'development') {
    options.headers['User'] = 1536;
    options.headers['SupplierUser'] = 86;
  }

  // @ts-ignore
  return fetch(Path.join('/api', url), otherOptions)
    .then((response: any) => {
      if (!response.ok) {
        const error = new Error(response.statusText);
        // @ts-ignore
        error.response = response;
        throw error;
      }

      const headers = response.headers;
      const contentDisposition = headers.get('Content-Disposition');
      // 如果返回文件，检测返回头是否有文件标识
      if (dataType === 'blob') {
        if (contentDisposition) {
          if (method === 'POST') {
            return postDownload(response);
          } else {
            return response[dataType]().then((data: any) => {
              if (options.fileName) {
                return data;
              }
            });
          }
        } else {
          dataType = 'json';
          return response.json();
        }
      }
      return response[dataType]();
    })
    .then((result: any) => {
      if (dataType === 'json') {
        // 兼容java接口
        if (_.isNil(result.res_code) && !_.isNil(result.code)) {
          result.res_code = result.code;
        }
        // 兼容java接口
        if (!result.res_msg && result.message) {
          result.res_msg = result.message;
        }

        if (result.res_code !== 1 && result.res_code !== -1) {
          const resMsg = Array.isArray(result.res_msg)
            ? result.res_msg.join(';')
            : result.res_msg;
          throw new Error(resMsg);
        }

        if (toast && toast.success) {
          showAlert({ type: 'success', message: result.res_msg });
        }
      }

      if (uuidKey) {
        removeRequestUUID(uuidKey);
      }

      return result;
    })
    .catch((error: any) => {
      showErrorAlert &&
      showAlert({
        type: 'error',
        message:
          typeof error === 'string' ? error : error.message || '未知错误',
      });
      throw new ServiceError(typeof error === 'string' ? error : error.message || '服务器错误')
    });
})

// https://stackoverflow.com/questions/16086162/handle-file-download-from-ajax-post
const postDownload = (response: any) => {
  let filename = '';
  const headers = response.headers;
  const contentDisposition = headers.get('Content-Disposition');

  if (contentDisposition && contentDisposition.indexOf('attachment') !== -1) {
    const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
    const matches = filenameRegex.exec(contentDisposition);
    if (matches != null && matches[1]) {
      filename = new Buffer(matches[1], 'ascii')
        .toString('utf-8')
        .replace(/['"]/g, '');
    }
  }

  response.blob &&
    response.blob().then((blob: any) => {
      if (typeof window.navigator.msSaveBlob !== 'undefined') {
        // IE workaround for "HTML7007: One or more blob URLs were revoked by
        // closing the blob for which they were created.
        // These URLs will no longer resolve as the data backing the URL has been freed."
        window.navigator.msSaveBlob(blob, filename);
      } else {
        // @ts-ignore
        const URL = window.URL || window.webkitURL;
        const downloadUrl = URL.createObjectURL(blob);
        if (filename) {
          // use HTML5 a[download] attribute to specify filename
          const a = document.createElement('a');
          // safari doesn't support this yet
          if (typeof a.download === 'undefined') {
            window.location.href = downloadUrl;
          } else {
            a.href = downloadUrl;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
          }
        } else {
          window.location.href = downloadUrl;
        }
        setTimeout(() => {
          URL.revokeObjectURL(downloadUrl);
        }, 100); // cleanup
      }
    });

  return response;
};
